/**
 * Shared API Client Service
 * Microservice-compatible centralized axios instance with authentication, token refresh, and error handling
 * Supports both user authentication (JWT) and service-to-service authentication (API keys)
 */

import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import {
  AUTH_API_CONFIG,
  CASE_MGMT_SERVICE_API_CONFIG,
  FORM_DATA_SERVICE_API_CONFIG,
} from '../config/api.config';
import { msalReady } from './msalBootstrap.service';
import { resolveBearerAccessTokenForApiCalls } from '../auth/resolveBearerAccessToken';
import { forceTokenRefresh } from '../utils/tokenCache.utils';
import { logApiCall, logApiError, logTokenRefresh } from '../utils/authTelemetry.utils';
import {
  shouldTriggerGenericApiErrorRedirect,
  triggerGenericApiErrorRedirect,
} from '../utils/apiErrorRedirect.utils';
export interface ServiceAuthConfig {
  apiKey?: string;
  apiKeyHeader?: string; // Default: 'X-API-Key'
  serviceToken?: string;
  serviceTokenHeader?: string; // Default: 'Authorization'
}

export interface ApiClientConfig {
  baseUrl?: string; // Override base URL for specific microservice
  timeout?: number;
  serviceAuth?: ServiceAuthConfig;
}

// Filled by session.service.ts via registerApiActivityCallback().
// Kept in a module-level slot (not imported from session.service.ts) to avoid
// circular dependency: session.service → apiClient.service → session.service.
let _activityCallback: (() => void) | undefined;

/**
 * Register a callback that fires on every outbound request across ALL axios
 * instances created by this module. Pass `undefined` to deregister (e.g. in tests).
 */
export function registerApiActivityCallback(cb: (() => void) | undefined): void {
  _activityCallback = cb;
}

// MSAL handles token refresh internally, no need for manual queue management

/**
 * Extract base URL from environment variable, removing any API path
 * Handles URLs with or without version paths (e.g., https://localhost:7043/api/v1)
 * Returns just the origin (e.g., https://localhost:7043)
 */
const extractBaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.origin; // Returns protocol + host + port, no path
  } catch {
    return url;
  }
};

/**
 * Get base URL for API client
 * In development, use relative URLs to leverage Vite proxy (bypasses CORS)
 * In production, use absolute URLs from environment variables (without API path)
 */
const getBaseUrlForClient = (envUrl: string, fallback: string): string => {
  // In production, extract base URL (remove any /api/v* path if present)
  // Endpoints already include the version path, so base URL should be just the origin
  return extractBaseUrl(envUrl || fallback);
};

/**
 * Create axios instance with authentication and token refresh interceptors
 * Supports both user JWT tokens and service-to-service authentication
 * Can be configured for different microservices (Data Access API, Auth API, Form Data Service, etc.)
 * 
 * @param config - Optional configuration for base URL, timeout, and service authentication
 * @returns Configured AxiosInstance
 */
export const createApiClient = (config?: ApiClientConfig | ServiceAuthConfig): AxiosInstance => {
  // Handle backward compatibility: if config is ServiceAuthConfig (old signature)
  let baseUrl: string;
  let timeout: number;
  let serviceAuth: ServiceAuthConfig | undefined;

  if (config && 'baseUrl' in config) {
    // New signature with ApiClientConfig
    // Use the provided baseUrl as-is (don't override to empty string in dev)
    const providedBaseUrl = config.baseUrl ?? FORM_DATA_SERVICE_API_CONFIG.baseUrl;
    baseUrl = extractBaseUrl(providedBaseUrl);
    timeout = config.timeout ?? FORM_DATA_SERVICE_API_CONFIG.timeout;
    serviceAuth = config.serviceAuth;
  } else {
    // Old signature with ServiceAuthConfig only (backward compatibility)
    baseUrl = getBaseUrlForClient(FORM_DATA_SERVICE_API_CONFIG.baseUrl, 'https://localhost:7194');
    timeout = FORM_DATA_SERVICE_API_CONFIG.timeout;
    serviceAuth = config as ServiceAuthConfig | undefined;
  }

  const client = axios.create({
    baseURL: baseUrl,
    timeout: timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Store the actual base URL for debugging (even if baseURL is empty in dev for proxy)
  // This allows inspection of the actual configured base URL
  const actualBaseUrl =
    config && 'baseUrl' in config
      ? (config.baseUrl ?? FORM_DATA_SERVICE_API_CONFIG.baseUrl)
      : FORM_DATA_SERVICE_API_CONFIG.baseUrl;
  (client as any).__actualBaseUrl = actualBaseUrl;

  // Request interceptor - Add authentication token
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders;
      }

      // Priority 1: Service-to-service authentication (API key or service token)
      if (serviceAuth) {
        if (serviceAuth.apiKey) {
          const headerName = serviceAuth.apiKeyHeader || 'X-API-Key';
          config.headers[headerName] = serviceAuth.apiKey;
          
          // Log API key usage in development (without exposing the key)
          if (import.meta.env.DEV) {
            console.log('[API Client] Using API key authentication:', {
              headerName,
              keyLength: serviceAuth.apiKey.length,
              keyPrefix: serviceAuth.apiKey.substring(0, 4) + '...',
              url: config.url,
            });
          }
          
          return config;
        }
        if (serviceAuth.serviceToken) {
          const headerName = serviceAuth.serviceTokenHeader || 'Authorization';
          config.headers[headerName] = `Bearer ${serviceAuth.serviceToken}`;
          
          if (import.meta.env.DEV) {
            console.log('[API Client] Using service token authentication:', {
              headerName,
              url: config.url,
            });
          }
          
          return config;
        }
      }

      // Priority 2: User JWT — same resolution as api.service / Form Export (context → shared → MSAL after bootstrap)
      try {
        const token = await resolveBearerAccessTokenForApiCalls();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          if (config.url) {
            logApiCall(config.url, config.method?.toUpperCase() || 'GET');
          }
        }
      } catch (error) {
        console.error('[API Client] Failed to resolve Bearer token:', error);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - Handle token refresh on 401 errors using MSAL
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Enhanced error logging for debugging
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;
        
        // Log detailed error information in development
        if (import.meta.env.DEV) {
          console.error('[API Client] Response error:', {
            status,
            statusText: error.response.statusText,
            url: error.config?.url,
            method: error.config?.method,
            responseData: responseData,
            headers: error.response.headers,
          });
          
          // For 400 errors, provide detailed validation error information
          if (status === 400) {
            console.error('[API Client] Bad Request (400) - Possible causes:');
            console.error('  1. Request payload validation failed');
            console.error('  2. Missing required fields');
            console.error('  3. Invalid field format or type');
            console.error('  4. Field value constraints violated');
            if (responseData) {
              console.error('  Backend validation errors:', JSON.stringify(responseData, null, 2));
              // Try to extract validation errors from response
              if (typeof responseData === 'object' && responseData !== null) {
                const data = responseData as Record<string, any>;
                if (data.errors && Array.isArray(data.errors)) {
                  console.error('  Validation error details:', data.errors);
                }
                if (data.message) {
                  console.error('  Error message:', data.message);
                }
                // Check for common validation error formats
                if (data.title) {
                  console.error('  Error title:', data.title);
                }
                if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
                  console.error('  Field validation errors:', data.errors);
                }
              }
            }
          }
          
          // For 500 errors, provide more context
          if (status === 500) {
            console.error('[API Client] Server error (500) - Possible causes:');
            console.error('  1. Backend endpoint not implemented');
            console.error('  2. Backend server error (check backend logs)');
            console.error('  3. Missing or invalid API key');
            console.error('  4. Backend expecting different request format');
            if (responseData) {
              console.error('  Backend error details:', responseData);
            }
          }
        }
      }

      // Log API error
      if (error.config?.url && error.response) {
        logApiError(
          error.config.url,
          error.message,
          error.response.status
        );
      }

      // If error is 401 and we haven't already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // For MSAL authentication, use MSAL token refresh
        if (AUTH_API_CONFIG.providerType === 'msal') {
          const msalInstance = await msalReady;

          if (msalInstance) {
            try {
              // Force token refresh via MSAL
              const token = await forceTokenRefresh(msalInstance);
              
              if (token) {
                // Update authorization header
                if (originalRequest.headers) {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                }
                
                logTokenRefresh(true);
                
                // Retry original request
                return client(originalRequest);
              } else {
                // No token available - user needs to log in
                logTokenRefresh(false, 'No accounts available');
                window.dispatchEvent(new CustomEvent('auth:logout'));
                return Promise.reject(error);
              }
            } catch (refreshError) {
              console.error('[API Client] Token refresh failed:', refreshError);
              logTokenRefresh(false, refreshError instanceof Error ? refreshError.message : 'Unknown error');
              
              // Check if interaction is required
              if (refreshError instanceof Error) {
                const errorMessage = refreshError.message.toLowerCase();
                if (errorMessage.includes('interaction_required') || 
                    errorMessage.includes('consent_required') ||
                    errorMessage.includes('login_required')) {
                  // User interaction required - trigger re-login
                  window.dispatchEvent(new CustomEvent('auth:logout'));
                }
              }
              
              return Promise.reject(error);
            }
          }
        }
      }

      if (shouldTriggerGenericApiErrorRedirect(error)) {
        triggerGenericApiErrorRedirect();
      }

      return Promise.reject(error);
    }
  );

  // Activity interceptor — added last so it executes first (axios request interceptors are LIFO).
  client.interceptors.request.use(
    (config) => {
      _activityCallback?.();
      return config;
    },
    undefined,
    { synchronous: true }
  );

  return client;
};

// Export singleton instance (user authentication) — defaults to Form Data Service base
export const apiClient = createApiClient({
  baseUrl: getBaseUrlForClient(FORM_DATA_SERVICE_API_CONFIG.baseUrl, 'https://localhost:7194'),
  timeout: FORM_DATA_SERVICE_API_CONFIG.timeout,
});

/**
 * Client for routes hosted on the Form Data Service base (e.g. legacy form document fetch).
 */
export const createDataAccessApiClient = (serviceAuth?: ServiceAuthConfig): AxiosInstance => {
  return createApiClient({
    baseUrl: getBaseUrlForClient(FORM_DATA_SERVICE_API_CONFIG.baseUrl, 'https://localhost:7194'),
    timeout: FORM_DATA_SERVICE_API_CONFIG.timeout,
    serviceAuth,
  });
};

/**
 * Create a Form Data Service API client
 * Used for form submission operations (save/submit form data to Cosmos DB)
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
export const createFormDataServiceApiClient = (serviceAuth?: ServiceAuthConfig): AxiosInstance => {
  return createApiClient({
    baseUrl: getBaseUrlForClient(
      FORM_DATA_SERVICE_API_CONFIG.baseUrl,
      'https://localhost:7194'
    ),
    timeout: FORM_DATA_SERVICE_API_CONFIG.timeout,
    serviceAuth,
  });
};

/**
 * Create an Auth API client
 * Used for authentication operations (login, logout, session, etc.)
 * In development: uses relative URLs (goes through Vite proxy)
 * In production: uses absolute URLs from environment
 */
export const createAuthApiClient = (serviceAuth?: ServiceAuthConfig): AxiosInstance => {
  return createApiClient({
    baseUrl: getBaseUrlForClient(
      AUTH_API_CONFIG.baseUrl,
      'https://auth-api.azure-api.net'
    ),
    timeout: AUTH_API_CONFIG.timeout,
    serviceAuth,
  });
};

/**
 * Create a CaseMgmtService API client
 * Used for exam form details and case management operations (page header, exam form details).
 * In development: uses relative URLs so Vite proxy can forward to CaseMgmt service (avoids CORS).
 * In production: uses absolute URLs from VITE_CASE_MGMT_SERVICE_API_BASE_URL.
 */
export const createCaseMgmtServiceApiClient = (serviceAuth?: ServiceAuthConfig): AxiosInstance => {
  const actualBaseUrl = CASE_MGMT_SERVICE_API_CONFIG.baseUrl || 'https://localhost:7093';
  const baseUrl =
    import.meta.env.DEV
      ? ''
      : extractBaseUrl(actualBaseUrl);
  
  const client = createApiClient({
    baseUrl,
    timeout: CASE_MGMT_SERVICE_API_CONFIG.timeout,
    serviceAuth,
  });
  
  (client as any).__actualBaseUrl = actualBaseUrl;
  
  return client;
};

/**
 * Create a service-to-service API client (for microservice communication)
 * This client uses API keys or service tokens instead of user JWT tokens
 * @deprecated Use createDataAccessApiClient, createFormDataServiceApiClient, or createAuthApiClient instead
 */
export const createServiceClient = (serviceAuth: ServiceAuthConfig): AxiosInstance => {
  return createApiClient(serviceAuth);
};

